import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Estado global do interceptor de loading — acessível por injeção ou import direto.
 */
class PendingRequestsState {
  private _pendingRequests = 0;
  private _pendingRequestsStatus = new ReplaySubject<boolean>(1);
  private _filteredUrlPatterns: RegExp[] = [];

  readonly pendingRequestsStatus$ = this._pendingRequestsStatus.asObservable();

  get filteredUrlPatterns(): RegExp[] {
    return this._filteredUrlPatterns;
  }

  increment(): void {
    this._pendingRequests++;
    if (this._pendingRequests === 1) {
      this._pendingRequestsStatus.next(true);
    }
  }

  decrement(): void {
    this._pendingRequests--;
    if (this._pendingRequests === 0) {
      this._pendingRequestsStatus.next(false);
    }
  }

  setStatus(status: boolean): void {
    this._pendingRequestsStatus.next(status);
  }

  shouldBypass(url: string): boolean {
    return this._filteredUrlPatterns.some(pattern => pattern.test(url));
  }
}

export const pendingRequestsState = new PendingRequestsState();

/**
 * Inicializa o listener de navegação para mostrar/ocultar o loading.
 * Chamado uma vez no provideAppInitializer.
 */
export function initPendingInterceptorNavigation(): void {
  const router = inject(Router);
  // Roda no provideAppInitializer, dentro de um contexto de injeção: o takeUntilDestroyed
  // amarra a assinatura ao ciclo de vida do injetor raiz em vez de deixá-la solta.
  router.events.pipe(takeUntilDestroyed()).subscribe(event => {
    if (event instanceof NavigationStart) {
      pendingRequestsState.setStatus(true);
    }
    if (event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationCancel) {
      pendingRequestsState.setStatus(false);
    }
  });
}

/**
 * HttpInterceptorFn — substitui o antigo PendingInterceptorService class-based.
 */
export const pendingInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const shouldBypass = pendingRequestsState.shouldBypass(req.url);

  if (!shouldBypass) {
    pendingRequestsState.increment();
  }

  return next(req).pipe(
    finalize(() => {
      if (!shouldBypass) {
        pendingRequestsState.decrement();
      }
    })
  );
};
